/**
 * DOC FONTS — parent-side font plumbing for baked doc slides.
 *
 * A bake (docBridge.ts) ships NO font binaries — only the stylesheet hrefs it found in the document
 * (the reply's `fontHrefs` / the root div's `data-bds-fonts`). Two consumers, two shapes:
 *
 * - registerDocFonts(hrefs) — shadow-root rendering. Shadow trees share the host document's
 *   FontFaceSet, so registering each @font-face ONCE on document.fonts serves every baked slide with
 *   zero per-slide payload. Each css2 stylesheet is fetched cross-origin (Google Fonts sends
 *   Access-Control-Allow-Origin: *), its @font-face blocks parsed, and each face added as a
 *   url()-source FontFace — the browser demand-loads only the unicode-range subsets the rendered text
 *   actually hits, exactly like a real <link> would, so registering a family does not download every
 *   script subset.
 *
 * - getDocFontRasterCss() — thumbnail rasterization. SVG image documents cannot fetch, so the raster
 *   path needs binaries INLINE as data: URLs (the cost the old per-snapshot bridge paid on every
 *   capture; now paid once per unique stylesheet, module-cached). Built in the background starting
 *   with the first registerDocFonts call; the getter is synchronous and returns null until every
 *   registered stylesheet has finished inlining, so a thumbnail never bakes with half its fonts.
 *   Null strictly means "no registration yet / inlining in flight"; the empty string means READY
 *   with nothing to inline. The distinction matters: a system-font deck registers zero stylesheets,
 *   and when that read as "not ready" (it once did) far zoom waited forever on fonts that do not
 *   exist and never reached the thumbnail tier.
 *
 * Everything is fire-and-forget: a failed fetch or malformed block degrades that face to the fallback
 * stack. Nothing here throws.
 */

import { thumbDbGetText, thumbDbPutText } from '../canvasEngine/benchTextures'

/** Hrefs already ingested — registerDocFonts is idempotent per href. */
const seenHrefs = new Set<string>()
/** family|weight|style|unicode-range keys already on document.fonts — distinct css2 URLs overlap families. */
const seenFaces = new Set<string>()
/** font-binary URL → data: URL, shared across stylesheets (the same woff2 recurs in many css2 responses). */
const dataUrlCache = new Map<string, Promise<string | null>>()

/** Inlined css accumulated per finished stylesheet (rule order across stylesheets is irrelevant to @font-face). */
const rasterParts: string[] = []
let rasterStarted = false
let pendingRaster = 0

interface ParsedFace {
  family: string
  style: string
  weight: string
  unicodeRange: string
  src: string
}

/**
 * Register every @font-face from the given stylesheet URLs on document.fonts, and kick off the
 * background data:-URL inlining that feeds getDocFontRasterCss(). Idempotent per href; never throws.
 * ANY call — new hrefs, repeat hrefs, or none at all — flips the raster machinery to "started", so a
 * system-font deck (empty href list on every bake) reads ready-empty ('') instead of null forever.
 */
export function registerDocFonts(hrefs: string[]): void {
  if (typeof document === 'undefined' || !document.fonts) return
  // The started flip must NOT live inside the loop: a system-font deck registers zero stylesheets,
  // and with the flip gated on a new href, getDocFontRasterCss() stayed null ("not ready") forever
  // and far zoom never left full static DOM for the thumbnail tier. Ready-empty is the honest
  // answer — the fonts are as ready as they will ever be, there just aren't any.
  rasterStarted = true
  for (const href of hrefs) {
    if (!href || seenHrefs.has(href)) continue
    seenHrefs.add(href)
    pendingRaster += 1
    void ingestStylesheet(href)
      .catch(() => undefined)
      .finally(() => {
        pendingRaster -= 1
      })
  }
}

/**
 * SYNCHRONOUS. The cached @font-face css with binaries inlined as data: URLs, for injection into
 * raster-side documents that cannot fetch. Null = not ready: registerDocFonts has never run, or a
 * registered stylesheet is still inlining (a later call with a new href flips back to null until the
 * newcomer lands — "not ready" is honest either way). Any string = ready; '' is the ready-empty
 * answer when nothing was inlinable or nothing was registered at all (system-font decks).
 */
export function getDocFontRasterCss(): string | null {
  if (!rasterStarted || pendingRaster > 0) return null
  return rasterParts.join('\n')
}

async function ingestStylesheet(href: string): Promise<void> {
  // Persistent fast path (thumb-cache KV): this stylesheet's fully-inlined css from a prior boot.
  // Rebuilding it costs a css2 fetch + one binary fetch and base64 per face, EVERY boot, strictly
  // before any doc thumbnail can arm — the hidden serial seconds in every reload. On a hit the faces
  // still register (from the cached data: URLs — zero network) and the raster css is ready at once.
  const cacheKey = `fontcss:${href}`
  try {
    const cached = await thumbDbGetText(cacheKey)
    if (cached) {
      registerFacesFrom(cached)
      rasterParts.push(cached)
      return
    }
  } catch {
    /* cache is an optimization — fall through to the network path */
  }
  let css: string
  try {
    // Bounded: getDocFontRasterCss() reports null while ANY ingest is in flight, and a single HUNG
    // fetch (a warming dev proxy, a stalled connection — measured as Guri's 10-15s frozen loader,
    // which was the 8s+6s safety net riding out exactly this) would otherwise pin "not ready"
    // indefinitely. Timeout lands in the existing catch: that stylesheet's faces fall back, the
    // raster css turns ready with what DID arrive.
    const res = await fetch(href, { signal: AbortSignal.timeout(4000) })
    css = await res.text()
  } catch {
    return // offline / blocked — faces fall back, raster css just omits them
  }
  // Registration first: it only needs the css text, so faces are usable long before the
  // (much heavier) binary inlining below settles.
  registerFacesFrom(css)
  const inlined = await inlineBinaries(css)
  if (inlined) {
    rasterParts.push(inlined)
    thumbDbPutText(cacheKey, inlined)
  }
}

/** Register every not-yet-seen @font-face from the given css on document.fonts. Idempotent per
 *  family|weight|style|range; src may carry https: or data: URLs — FontFace takes both. */
function registerFacesFrom(css: string): void {
  for (const face of parseFaces(css)) {
    const key = [
      face.family.toLowerCase(),
      face.weight,
      face.style,
      face.unicodeRange.replace(/\s+/g, '').toLowerCase(),
    ].join('|')
    if (seenFaces.has(key)) continue
    seenFaces.add(key)
    try {
      const desc: FontFaceDescriptors = { style: face.style, weight: face.weight }
      if (face.unicodeRange) desc.unicodeRange = face.unicodeRange
      document.fonts.add(new FontFace(face.family, face.src, desc))
    } catch {
      // malformed descriptor — that face falls back, the rest still land
    }
  }
}

/** Pull @font-face descriptors out of a css2 response. Regex, not CSSOM: the text was never attached
 *  to a document, and css2 output is flat enough (no nesting, no at-rule bodies inside blocks). */
function parseFaces(css: string): ParsedFace[] {
  const out: ParsedFace[] = []
  for (const block of css.match(/@font-face\s*\{[^}]*\}/g) ?? []) {
    const family = descriptor(block, 'font-family')?.replace(/^['"]|['"]$/g, '')
    const src = descriptor(block, 'src')
    if (!family || !src || !src.includes('url(')) continue
    out.push({
      family,
      style: descriptor(block, 'font-style') ?? 'normal',
      weight: descriptor(block, 'font-weight') ?? 'normal',
      unicodeRange: descriptor(block, 'unicode-range') ?? '',
      src, // FontFace takes the raw src descriptor value: url(...) format('woff2')
    })
  }
  return out
}

function descriptor(block: string, name: string): string | null {
  const m = block.match(new RegExp('(?:^|[\\s;{])' + name + '\\s*:\\s*([^;}]+)'))
  return m ? m[1].trim() : null
}

/**
 * Faces worth carrying into a RASTER. Google serves a family as one @font-face per unicode-range subset,
 * so a plain `Inter:wght@300;400` is 14 files: measured 427KB, of which latin is 95KB and the rest is
 * latin-ext 167KB plus cyrillic, greek and vietnamese. Every byte of that was being inlined into EVERY
 * slide's raster string. Registration on document.fonts (above) keeps all of them — the browser
 * demand-loads only what the text needs there, so real rendering is unaffected in any language. This
 * filter applies ONLY to the far-view bitmap, where a slide is at most 153px wide.
 *
 * KNOWN LIMIT, deliberate: a slide whose text needs a dropped subset (accented Eastern European, Greek,
 * Cyrillic, Georgian) renders those glyphs in a fallback face IN THE THUMBNAIL ONLY. If that ever shows
 * up, the honest fix is to select subsets from the characters actually present in the document rather
 * than widening this constant back out.
 */
const RASTER_SUBSET_MAX = 0x0100 // Basic Latin + Latin-1 Supplement
function keepFaceForRaster(block: string): boolean {
  const range = descriptor(block, 'unicode-range')
  if (!range) return true // no range declared → the face is the whole family, always keep
  for (const part of range.split(',')) {
    const start = /u\+([0-9a-f]+)/i.exec(part.trim())
    if (start && parseInt(start[1], 16) < RASTER_SUBSET_MAX) return true
  }
  return false
}

/** Replace every url(...) in the css with a fetched data: URL. Partial success is kept: a face whose
 *  binary failed keeps its remote url and simply falls back inside the raster document. */
async function inlineBinaries(rawCss: string): Promise<string | null> {
  const css = (rawCss.match(/@font-face\s*\{[^}]*\}/g) ?? []).filter(keepFaceForRaster).join('\n')
  const urls = [
    ...new Set((css.match(/url\(([^)]+)\)/g) ?? []).map((tok) => tok.slice(4, -1).replace(/["']/g, ''))),
  ]
  if (urls.length === 0) return null
  const pairs = await Promise.all(urls.map(async (u) => [u, await toDataUrl(u)] as const))
  let out = css
  for (const [from, to] of pairs) {
    if (to) out = out.split(from).join(to)
  }
  return out
}

function toDataUrl(url: string): Promise<string | null> {
  let p = dataUrlCache.get(url)
  if (!p) {
    p = fetch(url, { signal: AbortSignal.timeout(4000) }) // same bound as ingestStylesheet — a hung binary held the loader identically
      .then((r) => r.blob())
      .then(
        (b) =>
          new Promise<string | null>((resolve) => {
            const fr = new FileReader()
            fr.onload = () => resolve(typeof fr.result === 'string' ? fr.result : null)
            fr.onerror = () => resolve(null)
            fr.readAsDataURL(b)
          }),
      )
      .catch(() => null)
    dataUrlCache.set(url, p)
  }
  return p
}
